import React, { useRef, useState } from 'react';
import { ChevronDown, FileText, Image, MapPin, Mic, Sticker, Users, Video, MessageCircle, Search, UserRound, X } from 'lucide-react';

const ATTACHMENT_PREVIEWS = {
  document: [FileText, 'Document'],
  image: [Image, 'Photo'],
  audio: [Mic, 'Voice message'],
  video: [Video, 'Video'],
  sticker: [Sticker, 'Sticker'],
  contacts: [Users, 'Shared contacts'],
  location: [MapPin, 'Shared location'],
  unsupported: [MessageCircle, 'Message unavailable'],
};

const FILTERS = [
  ['open', 'Open conversations'],
  ['unread', 'Unread conversations'],
  ['mine', 'Assigned to me'],
  ['handover', 'Needs human follow-up'],
  ['unassigned', 'Unassigned'],
  ['resolved', 'Resolved'],
  ['all', 'All conversations'],
];

const formatTime = (value) => {
  if (!value) return 'No messages';
  try {
    const date = new Date(value);
    const options = date.toDateString() === new Date().toDateString() ? { hour: '2-digit', minute: '2-digit' } : { month: 'short', day: 'numeric' };
    return new Intl.DateTimeFormat(undefined, options).format(date);
  } catch {
    return value;
  }
};

const customerLabel = (conversation) => conversation.customer_name || conversation.customerName || conversation.customer_wa_id || conversation.customerWaId || 'WhatsApp customer';

const WhatsAppConversationList = ({
  conversations,
  filter,
  onFilterChange,
  search = '',
  onSearchChange,
  selectedId,
  onSelect,
  loading,
  fetching,
  error,
  nextCursor,
  onLoadMore,
  loadingMore,
}) => {
  const [searchOpen, setSearchOpen] = useState(Boolean(search));
  const searchButton = useRef(null);
  const showSearch = searchOpen || Boolean(search);
  const closeSearch = () => { onSearchChange?.(''); setSearchOpen(false); searchButton.current?.focus(); };
  return (
  <aside className="flex h-full min-h-0 flex-col border-r border-gray-200 bg-white">
    <div className="relative shrink-0 border-b border-gray-200 px-3 py-3">
      <div className="flex items-center justify-between gap-3">
        <div className="relative min-w-0 flex-1">
          <h2 className="sr-only">Conversations</h2>
          <select
            aria-label="Conversation view"
            value={filter}
            onChange={event => onFilterChange(event.target.value)}
            disabled={loading}
            className="min-h-[44px] w-full appearance-none truncate rounded-lg border-0 bg-transparent py-2 pl-1 pr-7 text-sm font-semibold text-gray-900 hover:bg-gray-50 disabled:opacity-50"
          >
            {FILTERS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <ChevronDown aria-hidden="true" className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
        </div>
        <div className="flex items-center gap-1">
          <button ref={searchButton} type="button" aria-label={showSearch ? 'Close search' : 'Search conversations'} aria-expanded={showSearch} aria-controls="whatsapp-search-panel" onClick={() => showSearch ? closeSearch() : setSearchOpen(true)} className="flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100">
            {showSearch ? <X className="h-5 w-5" /> : <Search className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <div
        role="progressbar"
        aria-label="Refreshing conversations"
        aria-hidden={!fetching || loading || loadingMore}
        className={`pointer-events-none absolute inset-x-0 bottom-0 h-0.5 transition-opacity duration-200 motion-reduce:transition-none ${fetching && !loading && !loadingMore ? 'opacity-100 delay-300' : 'opacity-0'}`}
      >
        <div className="h-full bg-primary-300 motion-safe:animate-pulse" />
      </div>
      {showSearch && <div id="whatsapp-search-panel" className="mt-2">
        <label className="sr-only" htmlFor="whatsapp-inbox-search">Search conversations</label>
        <input autoFocus id="whatsapp-inbox-search" type="search" value={search} maxLength={100} onChange={event => onSearchChange?.(event.target.value)} onKeyDown={event => { if (event.key === 'Escape') { event.preventDefault(); closeSearch(); } }} placeholder="Name or phone number" className="w-full min-w-0 rounded-lg border border-gray-300 p-2 text-sm" />
      </div>}
    </div>
    {error && <div role="alert" className="m-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700">{error}</div>}
    <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
      {loading ? (
        <div role="status">
          <span className="sr-only">Loading inbox...</span>
          <div aria-hidden="true" className="divide-y divide-gray-100 motion-safe:animate-pulse">
            {[0, 1, 2, 3, 4, 5].map(row => (
              <div key={row} className="flex items-center gap-3 px-4 py-3">
                <div className="h-10 w-10 shrink-0 rounded-full bg-gray-100" />
                <div className="min-w-0 flex-1 space-y-2">
                  <div className={`h-3 rounded-full bg-gray-200 ${row % 2 ? 'w-2/3' : 'w-1/2'}`} />
                  <div className="h-3 w-5/6 rounded-full bg-gray-100" />
                </div>
                <div className="h-2 w-7 shrink-0 rounded-full bg-gray-100" />
              </div>
            ))}
          </div>
        </div>
      ) : !error && conversations.length === 0 ? (
        <div className="m-4 rounded-lg border border-dashed border-gray-200 bg-gray-50 p-6 text-center">
          <MessageCircle className="mx-auto mb-3 h-8 w-8 text-gray-300" />
          <p className="font-semibold text-gray-800">{search ? 'No matching conversations.' : filter === 'mine' ? 'No conversations assigned to you.' : `No ${filter === 'all' ? '' : `${filter} `}conversations here.`}</p>
          <p className="mt-1 text-sm text-gray-500">{search ? 'Try a different name or phone number.' : 'Choose another filter, or wait for new WhatsApp messages.'}</p>
          {search && <button type="button" className="mt-3 underline text-primary-700" onClick={() => onSearchChange?.('')}>Clear search</button>}
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {conversations.map((conversation) => {
            const id = conversation.id;
            const preview = conversation.last_message_preview || conversation.lastMessagePreview || 'No preview available';
            const type = /^\[(document|image|audio|video|sticker|contacts|location|unsupported)(?: message)?\]$/i.exec(preview.trim())?.[1].toLowerCase();
            const [PreviewIcon, previewLabel] = ATTACHMENT_PREVIEWS[type] || [null, preview];
            const unread = Number(conversation.unread_count || conversation.unreadCount || 0);
            return (
              <button
                key={id}
                type="button"
                aria-current={selectedId === id ? 'true' : undefined}
                onClick={() => onSelect(id)}
                className={`inbox-conversation-row w-full px-4 py-4 text-left transition-colors hover:bg-primary-50/60 ${selectedId === id ? 'bg-primary-50' : 'bg-white'}`}
              >
                <div className="flex items-center gap-3">
                  <span aria-hidden="true" className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${unread ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-400'}`}><UserRound className="h-6 w-6" strokeWidth={1.75} /></span>
                  <div className="min-w-0 flex-1">
                    <p className={`truncate text-sm text-gray-900 ${unread ? 'font-bold' : 'font-semibold'}`}>{customerLabel(conversation)}</p>
                {(conversation.needs_human_review || conversation.needs_price_review) && <p className="text-xs font-medium text-amber-800">{conversation.handover_urgent ? 'Urgent · ' : ''}{conversation.needs_price_review ? 'Price confirmation needed' : 'Human follow-up needed'}</p>}
                    <p className="mt-0.5 flex min-w-0 items-center gap-1.5 text-sm text-gray-600">{PreviewIcon && <PreviewIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-gray-500" />}<span className="truncate">{previewLabel}</span></p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p title={new Date(conversation.last_message_at || conversation.lastMessageAt || conversation.updated_at || conversation.updatedAt).toLocaleString()} className="text-xs font-medium text-gray-500">{formatTime(conversation.last_message_at || conversation.lastMessageAt || conversation.updated_at || conversation.updatedAt)}</p>
                    {unread > 0 && <span className="mt-2 inline-flex min-w-[1.5rem] justify-center rounded-full bg-primary-600 px-2 py-0.5 text-xs font-bold text-white">{unread}</span>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
    {nextCursor && (
      <div className="border-t border-gray-200 p-3">
        <button
          type="button"
          onClick={onLoadMore}
          disabled={loadingMore}
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {loadingMore ? 'Loading...' : 'Load more conversations'}
        </button>
      </div>
    )}
  </aside>
  );
};

export default WhatsAppConversationList;
